import videojs from "video.js/core.es.js";
import type Player from "video.js/dist/types/player";
import type TimeTooltipClass from "video.js/dist/types/control-bar/progress-control/time-tooltip";

const TimeTooltip = videojs.getComponent(
  "TimeTooltip",
) as typeof TimeTooltipClass;

type UpdateArgs = [DOMRect, number, string];

/**
 * vjs TimeTooltip centres itself over the seek point (and clamps itself inside
 * the player at either end) from a width it measures in update(). Two things
 * make that measurement stale and leave the tip a few px off the thumb:
 *
 * - It writes the new time only AFTER measuring, so a change in text width
 *   (9:59 -> 10:00) is positioned with the old width. Writing first fixes it.
 * - Hovering the bar animates the holder's font-size (0.2s, which scales the
 *   tooltip), and the hover update lands mid-animation. While playing, the next
 *   timeupdate corrects it; while PAUSED nothing does. Re-run the last update
 *   once that transition ends.
 *
 * Registered under vjs's own name so PlayProgressBar and MouseTimeDisplay pick it up.
 */
class CenteredTimeTooltip extends TimeTooltip {
  declare lastUpdateArgs_: UpdateArgs | undefined;

  constructor(player: Player, options?: object) {
    super(player, options);
    // Component#on also takes (target, type, listener); the vjs types list only (type, listener).
    (this.on as (...args: unknown[]) => void)(
      player.el(),
      "transitionend",
      (event: TransitionEvent) => {
        if (
          this.lastUpdateArgs_ &&
          event.propertyName === "font-size" &&
          (event.target as Element).classList.contains("vjs-progress-holder") &&
          (event.target as Element).contains(this.el_)
        ) {
          this.update(...this.lastUpdateArgs_);
        }
      },
    );
  }

  update(seekBarRect: DOMRect, seekBarPoint: number, content: string) {
    this.lastUpdateArgs_ = [seekBarRect, seekBarPoint, content];
    this.write(content);
    super.update(seekBarRect, seekBarPoint, content);
  }
}

videojs.registerComponent("TimeTooltip", CenteredTimeTooltip);

export default CenteredTimeTooltip;
